import type { PickingStatus } from '@proerp/shared';

// Einheiten-Formatierung ist zentral in @proerp/shared (SSOT).
export { formatUnit } from '@proerp/shared';

/** Anzeige-Label + Badge-Ton je Kommissionier-Status. */
export const PICKING_STATUS_META: Record<PickingStatus, { label: string; tone: 'neutral' | 'warning' | 'info' | 'success' }> = {
  ENTWURF: { label: 'Entwurf', tone: 'neutral' },
  FREIGEGEBEN: { label: 'Freigegeben', tone: 'info' },
  IN_ARBEIT: { label: 'In Arbeit', tone: 'warning' },
  KOMMISSIONIERT: { label: 'Kommissioniert', tone: 'info' },
  ABGESCHLOSSEN: { label: 'Abgeschlossen', tone: 'success' },
  STORNIERT: { label: 'Storniert', tone: 'neutral' },
};

/**
 * Endzustände: hier ist nichts mehr zu tun. Abgeschlossene und stornierte Aufträge
 * sind die Historie des Lagers — sie stehen in der Modulliste standardmäßig NICHT
 * mehr drin (Anweisung Tech-Lead 2026-07-30), weil die Liste sonst mit der Zeit zu
 * einem Archiv wird, in dem die drei offenen Aufträge untergehen.
 */
export const PICKING_TERMINAL_STATUSES: readonly PickingStatus[] = ['ABGESCHLOSSEN', 'STORNIERT'];

export function isPickingOrderOpen(status: PickingStatus): boolean {
  return !PICKING_TERMINAL_STATUSES.includes(status);
}

/**
 * Auswahl des Status-Filters. `OFFEN` ist der Standard und blendet die Endzustände
 * aus; `ALL` holt sie zurück, und jeder Status ist einzeln wählbar — sonst käme man
 * an einen abgeschlossenen Auftrag nur über „Alle Status" heran.
 */
export type PickingStatusFilter = PickingStatus | 'OFFEN' | 'ALL';

export const PICKING_STATUS_FILTERS: readonly { value: PickingStatusFilter; label: string }[] = [
  { value: 'OFFEN', label: 'Offene Aufträge' },
  { value: 'ALL', label: 'Alle Status' },
  { value: 'ENTWURF', label: 'Entwurf' },
  { value: 'FREIGEGEBEN', label: 'Freigegeben' },
  { value: 'IN_ARBEIT', label: 'In Arbeit' },
  { value: 'KOMMISSIONIERT', label: 'Kommissioniert' },
  { value: 'ABGESCHLOSSEN', label: 'Abgeschlossen' },
  { value: 'STORNIERT', label: 'Storniert' },
];

/**
 * Löschbar (Soft-Delete, `commissioning.delete`): gesperrt, sobald der Lagerausgang
 * gebucht ist (ABGESCHLOSSEN) oder ein Lieferschein daraus gewandelt wurde — beides
 * spiegelt exakt den Guard im Backend (`PickingOrderService.remove`).
 */
export function isPickingOrderDeletable(order: { status: PickingStatus; deliveryNoteId: string | null }): boolean {
  return order.status !== 'ABGESCHLOSSEN' && !order.deliveryNoteId;
}

/** Trifft der Status-Filter auf diesen Auftrag zu? */
export function matchesPickingStatusFilter(status: PickingStatus, filter: PickingStatusFilter): boolean {
  if (filter === 'ALL') return true;
  if (filter === 'OFFEN') return isPickingOrderOpen(status);
  return status === filter;
}

/**
 * Freitextsuche der Modulliste: KO-Nummer UND Projekt (Nummer/Name) — niemand merkt
 * sich die KO-Nummer, gesucht wird nach dem Projekt.
 */
export function matchesPickingSearch(
  order: { number: string; projectCode?: string | null; projectName?: string | null },
  search: string,
): boolean {
  const needle = search.trim().toLowerCase();
  if (!needle) return true;
  return (
    order.number.toLowerCase().includes(needle) ||
    (order.projectCode?.toLowerCase().includes(needle) ?? false) ||
    (order.projectName?.toLowerCase().includes(needle) ?? false)
  );
}
